import type {
  DismissedDuplicatePerson,
  RecordHint,
  ResearchHelp,
  PossibleDuplicatePersonSuggestion,
  PID,
  RejectedTreeToTreeMatch,
} from '@fs/zion-tree-types'
import { usePossibleDuplicates, useDismissedPossibleDuplicates } from '@fs/zion-research-help'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useFeatureFlag } from '@fs/zion-flags'
import removeNulls from './removeNulls'
import useFocusPerson from './useFocusPerson'
import useRecordHints from './useRecordHints'
import useRejectedPersonMatches from './useRejectedPersonMatches'

export default function useResearchHelpData(
  personId: PID,
  researchHelp: ResearchHelp | undefined,
  isResearcherGroup: boolean | undefined,
  treeId?: string
): ResearchHelpData {
  const { focusPerson } = useFocusPerson()
  // We use the person ID here instead of the person so we can start fetching this data immediately. The focus person might not be fetched completely yet.
  // record-matches 502s on deleted persons (TREEWEB-11802); the hints UI is hidden for them anyway
  const {
    recordHints,
    mutateRecordHints,
    isLoading: loadingRecordHints,
  } = useRecordHints({
    personId: focusPerson?.deleted ? undefined : personId,
    treeId,
  })
  const {
    rejectedTreeToTreeMatches,
    loading: loadingRejectedTreeToTreeMatches,
    refetch: refetchRejectedTreeToTreeMatches,
  } = useRejectedPersonMatches(personId)
  const { canAccessCET } = useGroupsContext()
  const { isOn: treeWebDismissedT2tMatches } = useFeatureFlag('treeWeb_dismissed_t2t_matches')
  const filteredRejectedTreeToTreeMatches = canAccessCET && treeWebDismissedT2tMatches ? rejectedTreeToTreeMatches : []

  const {
    data: duplicatePersonDataProblems = [],
    refetch: refetchDuplicates,
    loading: loadingPossibleDuplicates,
  } = usePossibleDuplicates(personId, isResearcherGroup, treeId)
  const {
    data: duplicatePersonDataProblemsDismissed = [],
    refetch: refetchDismissedDuplicates,
    loading: loadingDismissedPossibleDuplicates,
  } = useDismissedPossibleDuplicates(personId, isResearcherGroup)

  const dismissedIds = duplicatePersonDataProblemsDismissed?.map((dismissed) => dismissed.contextId)
  const filteredDuplicatePersonDataProblems = treeId
    ? duplicatePersonDataProblems?.filter((dup) => {
        return !dismissedIds.includes(dup.contextId)
      })
    : duplicatePersonDataProblems

  const count =
    (researchHelp?.childDataProblems?.length ?? 0) +
    (researchHelp?.duplicateChildDataProblems?.length ?? 0) +
    (researchHelp?.duplicateSpouseDataProblems?.length ?? 0) +
    (researchHelp?.focusPerson?.dataQualityList?.length ?? 0) +
    (researchHelp?.focusPerson?.researchSuggestionList?.length ?? 0) +
    (researchHelp?.couples?.dataQualityList?.length ?? 0) +
    (researchHelp?.couples?.researchSuggestionList?.length ?? 0) +
    filteredDuplicatePersonDataProblems.length +
    (recordHints?.matches.length ?? 0)

  const dismissedCount =
    (researchHelp?.childDataProblemsDismissed?.length ?? 0) +
    (researchHelp?.duplicateChildDataProblemsDismissed?.length ?? 0) +
    (researchHelp?.focusPersonDismissed?.dataQualityList?.length ?? 0) +
    (researchHelp?.focusPersonDismissed?.researchSuggestionList?.length ?? 0) +
    (researchHelp?.couplesDismissed?.dataQualityList?.length ?? 0) +
    (researchHelp?.couplesDismissed?.researchSuggestionList?.length ?? 0) +
    duplicatePersonDataProblemsDismissed.length +
    (recordHints?.nonMatches.length ?? 0) +
    filteredRejectedTreeToTreeMatches.length

  return {
    ...researchHelp,
    duplicatePersonDataProblems: removeNulls(filteredDuplicatePersonDataProblems),
    duplicatePersonDataProblemsDismissed: removeNulls(duplicatePersonDataProblemsDismissed),
    recordHints: recordHints?.matches,
    recordHintsDismissed: recordHints?.nonMatches,
    rejectedTreeToTreeMatches: filteredRejectedTreeToTreeMatches,
    count,
    dismissedCount,
    refetchDuplicates,
    refetchDismissedDuplicates,
    refetchRecordHints: mutateRecordHints,
    refetchRejectedTreeToTreeMatches,
    loadingRecordHints,
    loadingPossibleDuplicates: !!loadingPossibleDuplicates,
    loadingDismissedPossibleDuplicates: !!loadingDismissedPossibleDuplicates,
    loadingRejectedTreeToTreeMatches,
    showRejectedTreeToTreeMatches: treeWebDismissedT2tMatches && canAccessCET,
  }
}

export type ResearchHelpData = ResearchHelp & {
  duplicatePersonDataProblems?: PossibleDuplicatePersonSuggestion[]
  duplicatePersonDataProblemsDismissed?: (PossibleDuplicatePersonSuggestion | DismissedDuplicatePerson)[]
  recordHints?: RecordHint[]
  recordHintsDismissed?: RecordHint[]
  rejectedTreeToTreeMatches?: RejectedTreeToTreeMatch[]
  count: number
  dismissedCount: number
  refetchDuplicates: ReturnType<typeof usePossibleDuplicates>['refetch']
  refetchDismissedDuplicates: ReturnType<typeof useDismissedPossibleDuplicates>['refetch']
  refetchRecordHints: ReturnType<typeof useRecordHints>['mutateRecordHints']
  refetchRejectedTreeToTreeMatches: ReturnType<typeof useRejectedPersonMatches>['refetch']
  loadingPossibleDuplicates: boolean
  loadingRecordHints: boolean
  loadingDismissedPossibleDuplicates: boolean
  loadingRejectedTreeToTreeMatches: boolean
  showRejectedTreeToTreeMatches?: boolean
}
